import React, { useMemo, useCallback, useState } from 'react';
import {
    TitleProps,
    Title,
    Icon,
    useIsBreakpointUp,
} from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import Link from '@soluto-design/link';

export interface TableProps {
    cols: string[];
    themes?: string[];
    content: (string | { label: string; href?: string; target?: string })[][][];
    titleTag?: TitleProps['component'];
    wrapperClassName?: string;
    headerClassName?: string;
    allowMobile?: boolean;
    disableThemes?: boolean;
}

const DEFAULT_THEMES = ['Secondary', 'Quaternary', 'Tertiary'];

function getColor(theme: string | null) {
    return ['light', 'dark'].includes(theme || '')
        ? 'bg-background-secondary'
        : 'bg-background-brand-primary';
}

function getContent(
    content:
        | string
        | {
              label: string;
              href?: string;
              target?: string;
          },
    falseOrNullIcon?: boolean,
) {
    let text: string | null = typeof content === 'string' ? content : null;

    if (typeof content === 'object') {
        if (content.href) {
            return {
                children: (
                    <Link
                        href={content.href}
                        target={content.target}
                        className="text-text-primary !font-regular !no-underline [&_strong]:!underline flex flex-col"
                        dangerouslySetInnerHTML={{
                            __html: content.label,
                        }}
                    />
                ),
            };
        }

        text = content?.label;
    }

    if (text === 'x' || text === 'true') {
        return {
            children: <Icon icon="advice-check" className="text-[24px]" />,
        };
    }

    if (falseOrNullIcon && (!text || text === 'false')) {
        return {
            children: (
                <Icon icon="advice-not-included" className="text-[24px]" />
            ),
        };
    }

    if (text && text !== 'false') {
        return {
            dangerouslySetInnerHTML: {
                __html: text.replaceAll(`\n`, '<br />'),
            },
        };
    }

    return {};
}

export default function Table({
    cols,
    themes,
    content,
    titleTag = 'span',
    wrapperClassName,
    headerClassName,
    allowMobile,
    disableThemes,
}: TableProps) {
    const isLgUp = useIsBreakpointUp('lg');
    const isMobile = allowMobile && !isLgUp;

    const [mobileActive, setMobileActive] = useState(1);

    const firstIsTitle = useMemo(() => {
        if (!cols?.length) return false;

        return typeof cols[0] === 'string' && !cols[0].trim();
    }, [cols]);

    const getTheme = useCallback(
        (i: number): string | null => {
            if (disableThemes) return 'light';

            if (i === 0) {
                return null;
            }

            const finalThemes = themes ?? DEFAULT_THEMES;
            return finalThemes[(i - 1) % finalThemes.length];
        },
        [themes, disableThemes],
    );

    return (
        <div role="table" className={cls('mt-xl relative', wrapperClassName)}>
            <div
                className={cls(
                    'sticky top-0 z-[2]',
                    isMobile && 'mb-xl',
                    headerClassName,
                )}
                role="rowgroup">
                <div
                    className={cls(
                        'cell-header flex justify-around !font-reckless font-xs md:!text-h400 items-stretch',
                    )}
                    role="row">
                    {cols.map((col, i) => {
                        if (isMobile && i === 0) return null;

                        const theme = getTheme(i);
                        const isActive = isMobile && i === mobileActive;

                        return (
                            <Title
                                component={titleTag}
                                key={`table-col-${i}`}
                                {...(isMobile &&
                                    !isActive && {
                                        role: 'button',
                                        onClick: () => setMobileActive(i),
                                    })}
                                className={cls(
                                    'cell text-text-primary py-12 px-4 md:px-8 flex flex-1 min-h-56 justify-center text-center items-center !font-reckless !font-regular',
                                    i === 0
                                        ? `bg-background-primary px-16 md:px-8 !min-w-[90px] text-left justify-start`
                                        : cls(
                                              (!isMobile || isActive) &&
                                                  getColor(theme),
                                              '!min-w-[74px]',
                                              isMobile &&
                                                  i > 1 &&
                                                  '!border-l-small border-base-200',
                                              isMobile &&
                                                  !isActive &&
                                                  'text-base-400 bg-base-100',
                                          ),
                                    firstIsTitle && 'text-center',
                                )}
                                role="columnheader"
                                data-theme={theme}>
                                {col?.includes('{logo}') ? (
                                    <Icon
                                        icon="brand-logo"
                                        className="text-[20px]"
                                    />
                                ) : (
                                    col
                                )}
                            </Title>
                        );
                    })}
                </div>
            </div>
            {content.map((group, i) => (
                <div
                    key={`table-content-${i}`}
                    className={cls(i !== content.length - 1 && 'pb-xl')}
                    role="rowgroup">
                    {group.map((row, j) =>
                        (row.length === 1 && cols.length > 1) ||
                        (isMobile && typeof row[0] === 'string') ? (
                            <div
                                key={`table-content-${i}-${j}`}
                                role="row"
                                className={cls(
                                    'lg:w-full bg-background-secondary text-b200 px-24 py-16',
                                    isMobile && 'text-center',
                                )}
                                {...getContent(row[0])}
                            />
                        ) : (
                            <div
                                key={`table-content-${i}-${j}`}
                                className={cls(
                                    'flex justify-around !border-b-small border-stroke-secondary w-full min-h-56 items-stretch',
                                    isMobile && 'flex-col py-16',
                                    isMobile &&
                                        row.length > 1 &&
                                        (!row[mobileActive]?.label ||
                                            row[mobileActive].label ===
                                                'false') &&
                                        '[&_.cell]:opacity-20',
                                )}
                                role="row">
                                {row.map((cell, k) => {
                                    const titleCell =
                                        !isMobile &&
                                        k === 0 &&
                                        cell?.label?.includes(
                                            'data-title-cell',
                                        );
                                    const theme = getTheme(k);

                                    return (
                                        <div
                                            key={`table-content-${i}-${j}-${k}`}
                                            className={cls(
                                                'cell cell-content text-b400 py-8 px-4 md:px-8 flex-1 items-center content-center justify-center flex flex-col',
                                                k === 0 && !isMobile
                                                    ? 'bg-background-primary px-16 md:pl-24 !w-[90px] md:w-full items-start'
                                                    : cls(
                                                          !isMobile &&
                                                              getColor(theme),
                                                          'text-center items-center justify-center md:pl-24 md:pr-24 w-[75px] md:w-full',
                                                      ),
                                                (cell === 'x' ||
                                                    cell === 'true') &&
                                                    'flex justify-center items-center',
                                                titleCell && `md:pr-24 py-16`,
                                                isMobile && 'w-full',
                                                isMobile &&
                                                    k > 0 &&
                                                    `text-c100 text-text-secondary ${
                                                        k !== mobileActive
                                                            ? 'hidden'
                                                            : ''
                                                    }`,
                                            )}
                                            role="cell"
                                            data-theme={theme}
                                            {...getContent(cell, isMobile)}
                                        />
                                    );
                                })}
                            </div>
                        ),
                    )}
                </div>
            ))}
        </div>
    );
}
